import {
    ProductOutlined,
    SkinOutlined,
    ShoppingOutlined,
    TransactionOutlined,
    AuditOutlined,
    SettingOutlined,
    HomeOutlined,
    StarFilled,
} from '@ant-design/icons';
import Link from 'next/link';
import {
    DynamicMenuConfig,
    DynamicMenuConfigParams,
    NewItemType,
} from '@/lib/types';
import SvgIcon from '@/components/ui/svg-icon';

// 基础菜单配置 - 不包含动态内容
export const baseMenuConfig: {
    displayOnKeys: string[];
    menus: NewItemType[];
}[] = [
    // displayOnKeys 里的路径，不能再次出现在其它的 displayOnKeys 中
    // 比如 arr[0] = { displayOnKeys: ['/', '/orders'], menus: [...]}
    // 则 arr[1] 的 displayOnKeys 中 不能有 '/orders' 这个路径
    // TODO：这个规则是否有更好的办法？

    // ####### 首页菜单 #########################################################
    {
        // 首页要显示的菜单
        displayOnKeys: ['/'],
        menus: [
            {
                key: '/',
                label: (
                    <Link href="/" title="首页">
                        首页
                    </Link>
                ),
                icon: <HomeOutlined />,
            },
            {
                key: '/product',
                label: '产品',
                icon: <ProductOutlined />,
                children: [
                    {
                        key: '/products',
                        label: (
                            <Link href="/products" title="产品列表">
                                产品列表
                            </Link>
                        ),
                    },
                ],
            },
            {
                key: '/productions',
                label: '生产',
                icon: <SkinOutlined />,
                children: [
                    {
                        key: '/productions/order-list',
                        label: (
                            <Link href="/productions/order-list" title="订单">
                                订单
                            </Link>
                        ),
                    },
                    {
                        // 库存的主页面，库存下还分“产品”、“物料”、“面料”等库存，菜单在页面中，独立菜单
                        key: '/inventories',
                        label: '库存',
                        children: [
                            {
                                key: '/inventories/products',
                                label: (
                                    <Link
                                        href="/inventories/products"
                                        title="产品库存"
                                    >
                                        产品库存
                                    </Link>
                                ),
                            },
                            {
                                key: '/inventories/materials',
                                label: (
                                    <Link
                                        href="/inventories/materials"
                                        title="物料库存"
                                    >
                                        物料库存
                                    </Link>
                                ),
                            },
                            {
                                key: '/inventories/fabrics',
                                label: (
                                    <Link
                                        href="/inventories/fabrics"
                                        title="面料库存"
                                    >
                                        面料库存
                                    </Link>
                                ),
                            },
                        ],
                    },
                    {
                        key: '/materials',
                        label: (
                            <Link href="/materials" title="物料">
                                物料
                            </Link>
                        ),
                    },
                    {
                        key: '/material-purchase-orders/list',
                        label: (
                            <Link
                                href="/material-purchase-orders/list"
                                title="物料采购单"
                            >
                                物料采购单
                            </Link>
                        ),
                    },
                ],
            },
            // {
            //     key: '/sales',
            //     label: '销售',
            //     icon: <ShoppingOutlined />,
            //     children: [
            //         {
            //             key: '/sales/orders',
            //             label: (
            //                 <Link href="/sales/orders" title="订单">
            //                     订单
            //                 </Link>
            //             ),
            //         },
            //         {
            //             key: '/shipped-packages/package-list',
            //             label: (
            //                 <Link
            //                     href="/shipped-packages/package-list"
            //                     title="已发包裹"
            //                 >
            //                     已发包裹
            //                 </Link>
            //             ),
            //         },
            //         {
            //             key: '/refunds/refund-list',
            //             label: (
            //                 <Link href="/refunds/refund-list" title="退货处理">
            //                     退货处理
            //                 </Link>
            //             ),
            //         },
            //         {
            //             key: '/shipment-requests/list',
            //             label: (
            //                 <Link
            //                     href="/shipment-requests/list"
            //                     title="发货申请"
            //                 >
            //                     发货申请
            //                 </Link>
            //             ),
            //         },
            //     ],
            // },
            // {
            //     key: '/finances',
            //     label: (
            //         <Link href="/finances" title="财务">
            //             财务
            //         </Link>
            //     ),
            //     icon: <TransactionOutlined />,
            // },
            // {
            //     key: '/works',
            //     label: (
            //         <Link href="/works" title="工作">
            //             工作
            //         </Link>
            //     ),
            //     icon: <AuditOutlined />,
            // },
        ],
    },
];

// 生成动态菜单配置
export const getDynamicMenuConfig = (
    params?: DynamicMenuConfigParams,
): DynamicMenuConfig => {
    const config: DynamicMenuConfig = {
        home: baseMenuConfig[0], // 首页菜单
    };

    // 管理员菜单（仅超级管理员可见）
    // if (params?.user?.isAdmin) { // 屏蔽仅管理员可见的限制，普通用户通过申请权限处理
    config.admin = {
        displayOnKeys: ['/control-panel'],
        menus: [
            {
                key: '/control-panel',
                label: (
                    <Link href="/control-panel" title="设置">
                        设置
                    </Link>
                ),
                icon: <SettingOutlined />,
            },
        ],
    };
    // }

    // 收藏菜单（有收藏项时显示）
    if (params?.favorites && params.favorites.length > 0) {
        config.favorites = {
            displayOnKeys: ['favorites'],
            menus: [
                {
                    key: 'favorites',
                    label: '我的收藏',
                    icon: <StarFilled />,
                    children: params.favorites.map(favorite => ({
                        key: `favorite-${favorite.key}`,
                        label: (
                            <Link href={favorite.key} title={favorite.label}>
                                {favorite.label}
                            </Link>
                        ),
                    })),
                },
            ],
        };
    }

    return config;
};

export const settingsMenuChildren: NewItemType[] = [
    {
        key: '/control-panel/companies',
        title: '企业管理',
        label: (
            <Link href="/control-panel/companies" title="企业管理">
                企业管理
            </Link>
        ),
        hideInMenu: true,
        icon: (
            <SvgIcon
                svgUrl="/assets/img/icons/setting/company-manage.svg"
                color="var(--foreground)"
            />
        ),
    } as NewItemType,
    {
        key: '/control-panel/departments',
        title: '部门管理',
        label: (
            <Link href="/control-panel/departments" title="部门管理">
                部门管理
            </Link>
        ),
        hideInMenu: true,
        icon: (
            <SvgIcon
                svgUrl="/assets/img/icons/setting/department-manage.svg"
                color="var(--foreground)"
            />
        ),
    } as NewItemType,
    {
        key: '/control-panel/users',
        title: '用户管理',
        label: (
            <Link href="/control-panel/users" title="用户管理">
                用户管理
            </Link>
        ),
        hideInMenu: true,
        icon: (
            <SvgIcon
                svgUrl="/assets/img/icons/setting/user-manage.svg"
                color="var(--foreground)"
            />
        ),
    } as NewItemType,
    {
        key: '/control-panel/roles',
        title: '角色管理',
        label: (
            <Link href="/control-panel/roles" title="角色管理">
                角色管理
            </Link>
        ),
        hideInMenu: true,
        icon: (
            <SvgIcon
                svgUrl="/assets/img/icons/setting/role-manage.svg"
                color="var(--foreground)"
            />
        ),
    } as NewItemType,
    {
        key: '/control-panel/storage-locations',
        title: '存储地管理',
        label: (
            <Link href="/control-panel/storage-locations" title="存储地管理">
                存储地管理
            </Link>
        ),
        hideInMenu: true,
        icon: (
            <SvgIcon
                svgUrl="/assets/img/icons/setting/storage-location-manage.svg"
                color="var(--foreground)"
            />
        ),
    } as NewItemType,
    {
        key: '/control-panel/desen-rules',
        title: '字段脱敏',
        label: (
            <Link href="/control-panel/desen-rules" title="字段脱敏">
                字段脱敏
            </Link>
        ),
        hideInMenu: true,
        icon: (
            <SvgIcon
                svgUrl="/assets/img/icons/setting/desen-rules.svg"
                color="var(--foreground)"
            />
        ),
    } as NewItemType,
    {
        key: '/control-panel/color-families',
        title: '色系管理',
        label: (
            <Link href="/control-panel/color-families" title="产品色系管理">
                色系管理
            </Link>
        ),
        hideInMenu: true,
        icon: (
            <SvgIcon
                svgUrl="/assets/img/icons/setting/color-families.svg"
                color="var(--foreground)"
            />
        ),
    } as NewItemType,
    {
        key: '/control-panel/part-types',
        title: '部件类型管理',
        label: (
            <Link href="/control-panel/part-types" title="部件类型管理">
                部件类型管理
            </Link>
        ),
        hideInMenu: true,
        icon: (
            <SvgIcon
                svgUrl="/assets/img/icons/setting/products/product-part-types.svg"
                color="var(--foreground)"
            />
        ),
    } as NewItemType,
    {
        key: '/control-panel/body-parts',
        title: '成衣部位管理',
        label: (
            <Link href="/control-panel/body-parts" title="成衣部位管理">
                成衣部位管理
            </Link>
        ),
        hideInMenu: true,
        icon: (
            <SvgIcon
                svgUrl="/assets/img/icons/setting/products/product-body-parts.svg"
                color="var(--foreground)"
            />
        ),
    } as NewItemType,
    {
        key: '/control-panel/product-types',
        title: '产品类型管理',
        label: (
            <Link href="/control-panel/product-types" title="产品类型管理">
                产品类型管理
            </Link>
        ),
        hideInMenu: true,
        icon: (
            <SvgIcon
                svgUrl="/assets/img/icons/setting/products/product-types.svg"
                color="var(--foreground)"
            />
        ),
    } as NewItemType,
    {
        key: '/control-panel/product-attributes',
        title: '预设属性管理',
        label: (
            <Link href="/control-panel/product-attributes" title="预设属性管理">
                预设属性管理
            </Link>
        ),
        hideInMenu: true,
        icon: (
            <SvgIcon
                svgUrl="/assets/img/icons/setting/products/product-attributes.svg"
                color="var(--foreground)"
            />
        ),
    } as NewItemType,
    {
        key: '/control-panel/product-categories',
        title: '产品分类管理',
        label: (
            <Link href="/control-panel/product-categories" title="产品分类管理">
                产品分类管理
            </Link>
        ),
        hideInMenu: true,
        icon: (
            <SvgIcon
                svgUrl="/assets/img/icons/setting/products/product-categories.svg"
                color="var(--foreground)"
            />
        ),
    } as NewItemType,
    {
        key: '/control-panel/product-brands',
        title: '产品品牌管理',
        label: (
            <Link href="/control-panel/product-brands" title="产品品牌管理">
                产品品牌管理
            </Link>
        ),
        hideInMenu: true,
        icon: (
            <SvgIcon
                svgUrl="/assets/img/icons/setting/products/product-brands.svg"
                color="var(--foreground)"
            />
        ),
    } as NewItemType,
    // {
    //     key: '/control-panel/product-tags',
    //     title: '产品标签管理',
    //     label: (
    //         <Link href="/control-panel/product-tags" title="产品标签管理">
    //             产品标签管理
    //         </Link>
    //     ),
    //     hideInMenu: true,
    //     icon: (
    //         <SvgIcon
    //             svgUrl="/assets/img/icons/setting/products/product-tags.svg"
    //             color="var(--foreground)"
    //         />
    //     ),
    // } as NewItemType,
    {
        key: '/control-panel/product-sizes',
        title: '公共尺码管理',
        label: (
            <Link href="/control-panel/product-sizes" title="产品尺寸管理">
                公共尺码管理
            </Link>
        ),
        hideInMenu: true,
        icon: (
            <SvgIcon
                svgUrl="/assets/img/icons/setting/products/product-sizes.svg"
                color="var(--foreground)"
            />
        ),
    } as NewItemType,
    {
        key: '/control-panel/garment-regions',
        title: '标牌地区管理',
        label: (
            <Link href="/control-panel/garment-regions" title="标牌地区管理">
                标牌地区管理
            </Link>
        ),
        hideInMenu: true,
        icon: (
            <SvgIcon
                svgUrl="/assets/img/icons/setting/products/product-size-regions.svg"
                color="var(--foreground)"
            />
        ),
    } as NewItemType,
    {
        key: '/control-panel/garment-attributes',
        title: '标牌属性管理',
        label: (
            <Link href="/control-panel/garment-attributes" title="标牌属性管理">
                标牌属性管理
            </Link>
        ),
        hideInMenu: true,
        icon: (
            <SvgIcon
                svgUrl="/assets/img/icons/setting/products/product-attributes.svg"
                color="var(--foreground)"
            />
        ),
    } as NewItemType,
    {
        key: '/control-panel/hang-tag-padding-templates',
        title: '标牌填充物模板',
        label: (
            <Link
                href="/control-panel/hang-tag-padding-templates"
                title="标牌填充物模板"
            >
                标牌填充物模板
            </Link>
        ),
        hideInMenu: true,
        icon: (
            <SvgIcon
                svgUrl="/assets/img/icons/setting/carelabel/carelabel-setting.svg"
                color="var(--foreground)"
            />
        ),
    } as NewItemType,
    {
        key: '/control-panel/brand-extensions',
        title: '品牌扩充信息',
        label: (
            <Link href="/control-panel/brand-extensions" title="品牌扩充信息">
                品牌扩充信息
            </Link>
        ),
        hideInMenu: true,
        icon: (
            <SvgIcon
                svgUrl="/assets/img/icons/setting/products/product-brands-extras.svg"
                color="var(--foreground)"
            />
        ),
    } as NewItemType,
    {
        key: '/control-panel/wash-symbol-groups',
        title: '水洗标志分组',
        label: (
            <Link href="/control-panel/wash-symbol-groups" title="水洗标志分组">
                水洗标志分组
            </Link>
        ),
        hideInMenu: true,
        icon: (
            <SvgIcon
                svgUrl="/assets/img/icons/setting/carelabel/wash-symbol.svg"
                color="var(--foreground)"
            />
        ),
    } as NewItemType,

    {
        key: '/control-panel/material-types',
        title: '物料分类管理',
        label: (
            <Link href="/control-panel/material-types" title="物料分类管理">
                物料分类管理
            </Link>
        ),
        hideInMenu: true,
        icon: (
            <SvgIcon
                svgUrl="/assets/img/icons/setting/materials/material-types.svg"
                color="var(--foreground)"
            />
        ),
    },
    {
        key: '/control-panel/suppliers',
        title: '供应商管理',
        label: (
            <Link href="/control-panel/suppliers" title="供应商管理">
                供应商管理
            </Link>
        ),
        hideInMenu: true,
        icon: (
            <SvgIcon
                svgUrl="/assets/img/icons/setting/materials/suppliers.svg"
                color="var(--foreground)"
            />
        ),
    },
];

// 获取匹配的菜单配置
export const getMatchedMenuConfig = (
    pathname: string,
    dynamicConfig: DynamicMenuConfig,
): NewItemType[] => {
    // 按优先级匹配：收藏 -> 管理员 -> 首页
    if (dynamicConfig.favorites && pathname.includes('favorite')) {
        return dynamicConfig.favorites.menus;
    }

    if (dynamicConfig.admin && pathname.startsWith('/control-panel')) {
        return dynamicConfig.admin.menus;
    }

    return dynamicConfig.home.menus;
};

// 向后兼容的导出
export const menuConfig = baseMenuConfig;

/**
 * 获取设置页面的子菜单项
 * @param params 动态菜单配置参数
 * @returns 设置页面的子菜单项数组
 */
export const getSettingsMenuChildren = (
    params?: DynamicMenuConfigParams,
): NewItemType[] => {
    const dynamicConfig = getDynamicMenuConfig(params);
    if (dynamicConfig.admin && dynamicConfig.admin.menus.length > 0) {
        const settingsMenu = dynamicConfig.admin.menus.find(
            item => item.key === '/control-panel',
        );
        if (
            settingsMenu &&
            'children' in settingsMenu &&
            settingsMenu.children
        ) {
            return settingsMenu.children as NewItemType[];
        }
    }
    return [];
};
